<script setup lang="ts">
/**
 * Вкладка «История»: кто, когда и что поменял.
 *
 * Строка собирается из поля и двух значений: «Анна сменила статус: К работе →
 * В работе». Значения показываем как есть — имена людей и колонок сервис
 * подставил сам, а перечисления переводим по словарю: `PRIORITY_HIGH` в
 * истории читается не лучше, чем в коде.
 *
 * Длинные значения (описание) не показываем целиком: история отвечает на
 * вопрос «что менялось», а не «каким было описание в прошлый вторник».
 */
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { formatStamp } from '@/features/issues/format'

import { listHistory, type HistoryEntry } from '../history'
import { useIssueStore } from '../store'

/** Поля, значения которых лежат в словарях: их машинный вид не для людей */
const ENUM_FIELDS: Record<string, string> = {
  type: 'issues.types',
  priority: 'issues.priorities',
}

/** Столько знаков значения помещается в строку истории */
const VALUE_LIMIT = 80

const emit = defineEmits<{ failed: [error: unknown] }>()

const { t, te, locale } = useI18n()
const store = useIssueStore()

const entries = ref<HistoryEntry[]>([])
const total = ref(0)
const page = ref(1)
const isLoading = ref(false)

const issueId = computed(() => store.issue?.id ?? '')

const hasMore = computed(() => entries.value.length < total.value)

const load = async (next = false) => {
  if (!issueId.value) return

  isLoading.value = true
  page.value = next ? page.value + 1 : 1

  try {
    const chunk = await listHistory(issueId.value, page.value)

    entries.value = next ? [...entries.value, ...chunk.entries] : chunk.entries
    total.value = chunk.total
  } catch (failure) {
    emit('failed', failure)
  } finally {
    isLoading.value = false
  }
}

const fieldName = (field: string) =>
  te(`issue.history.fields.${field}`) ? t(`issue.history.fields.${field}`) : field

const value = (entry: HistoryEntry, raw: string) => {
  if (!raw) return t('issue.history.empty')

  const dictionary = ENUM_FIELDS[entry.field]

  if (dictionary && te(`${dictionary}.${raw}`)) return t(`${dictionary}.${raw}`)

  return raw.length > VALUE_LIMIT ? `${raw.slice(0, VALUE_LIMIT)}…` : raw
}

const stamp = (entry: HistoryEntry) =>
  formatStamp(entry.createdAt, locale.value, {
    today: t('issue.today'),
    yesterday: t('issue.yesterday'),
  })

onMounted(() => load())

watch(issueId, () => load())
</script>

<template>
  <section class="ihist">
    <p v-if="!entries.length && !isLoading" class="ihist__empty">
      {{ t('issue.history.none') }}
    </p>

    <div v-for="entry in entries" :key="entry.id" class="hrow">
      <span class="hrow__time">{{ stamp(entry) }}</span>

      <span class="hrow__text">
        <span class="hrow__actor">{{ entry.actorName || t('issue.history.system') }}</span>

        <template v-if="entry.field === 'created'">{{ t('issue.history.created') }}</template>

        <template v-else>
          {{ t('issue.history.changed', { field: fieldName(entry.field) }) }}
          <span class="hrow__old">{{ value(entry, entry.oldValue) }}</span>
          <span class="hrow__arrow">→</span>
          <span class="hrow__new">{{ value(entry, entry.newValue) }}</span>
        </template>
      </span>
    </div>

    <button v-if="hasMore" type="button" class="ihist__more" @click="load(true)">
      {{ t('issue.history.more') }}
    </button>
  </section>
</template>

<style scoped>
.ihist {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ihist__empty {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.hrow {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

/* Время слева колонкой: события читают сверху вниз, и ровный левый край
   позволяет пробежать глазами по датам, не читая сами строки. */
.hrow__time {
  flex-shrink: 0;
  width: 120px;
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
}

.hrow__text {
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
  line-height: 18px;
}

.hrow__actor {
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
}

.hrow__old {
  color: var(--color-text-muted);
  text-decoration: line-through;
}

.hrow__arrow {
  color: var(--color-text-muted);
}

.hrow__new {
  color: var(--color-text-primary);
}

.ihist__more {
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}
</style>
